import { createContext, RefObject } from 'preact';
import {
  getTabbableElements,
  FOCUSABLE_ELEMENT_SELECTOR,
  trapFocusBetweenElements,
} from '@uw/utils';
import { useCallback, useContext, useEffect, useState } from 'preact/hooks';

export enum FocusableTrapElement {
  FirstElement = 'setFirstFocusableElement',
  LastElement = 'setLastFocusableElement',
}

export const FocusTrapContext = createContext<ReturnType<typeof useFocusTrap>>({
  setFirstFocusableElement: () => {},
  setLastFocusableElement: () => {},
});

export const useFocusableElement = <TElement extends HTMLElement>(
  focusableElement: FocusableTrapElement,
  refToConnect?: RefObject<TElement>,
  ...dependencies: any[]
): ((node: TElement | null) => void) => {
  const context = useContext(FocusTrapContext);

  return useCallback(
    (node: TElement | null) => {
      if (refToConnect) {
        refToConnect.current = node;
      }

      if (!node) {
        return;
      }

      let element: HTMLElement | null = null;
      let elements: HTMLElement[] = getTabbableElements(node);

      if (node.matches(FOCUSABLE_ELEMENT_SELECTOR)) {
        elements = [node, ...elements];
      }

      if (focusableElement === FocusableTrapElement.FirstElement) {
        element = elements[0];
      } else {
        element = elements[elements.length - 1];
      }

      context[focusableElement](element);
    },
    [context, ...dependencies],
  );
};

export const useFocusTrap = () => {
  const [firstFocusableElement, setFirstFocusableElement] = useState<HTMLElement | null>(null);
  const [lastFocusableElement, setLastFocusableElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    let terminateTrapFocus: () => void | undefined;
    if (firstFocusableElement && lastFocusableElement) {
      const { terminate } = trapFocusBetweenElements(firstFocusableElement, lastFocusableElement);
      terminateTrapFocus = terminate;
    }

    return () => {
      if (terminateTrapFocus) {
        terminateTrapFocus();
      }
    };
  }, [firstFocusableElement, lastFocusableElement]);

  return {
    setFirstFocusableElement,
    setLastFocusableElement,
  };
};
